import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import cx from 'classnames';
import { Alert, InputGroup, Menu } from '@storable/canaveral-components';
import { MANAGE } from '../../../modules/authorization/actions';
import { BUILDER } from '../../../modules/authorization/subjects';
import useDebounce from '../../../modules/react/hooks/useDebounce';
import useCurrentUser from '../../hooks/useCurrentUser';
import { escapeAllSpecialCharacters } from '../../../modules/search';
import styles from './styles.scss';


const propTypes = {
  className: PropTypes.string,
  componentClass: PropTypes.element.isRequired,
  enableSearch: PropTypes.boolean,
  loading: PropTypes.bool,
  onSearch: PropTypes.func,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  title: PropTypes.string,
  totalPages: PropTypes.number,
};

const defaultProps = {
  className: undefined,
  enableSearch: undefined,
  title: undefined,
  onSearch: undefined,
  loading: undefined,
  totalPages: undefined,
};

const PagesMenu = ({
  className,
  componentClass: PagesMenuItem,
  enableSearch,
  loading,
  onSearch,
  pages,
  title,
  totalPages,
  ...rest
}) => {
  const currentUser = useCurrentUser();
  const [value, setValue] = useState('');
  const [toggled, setToggled] = useState(false);
  const debouncedValue = useDebounce(value);

  const onChangeSearch = (e) => {
    const escapedValue = escapeAllSpecialCharacters(e.target.value);
    setValue(escapedValue);
  };
  const onChangeToggle = () => setToggled(!toggled);
  const onClear = value ? () => setValue('') : undefined;

  useEffect(() => {
    if (!enableSearch) {
      return;
    }

    onSearch(debouncedValue);
  }, [debouncedValue]);

  const unauthorized = currentUser.cannot(MANAGE, BUILDER);

  const countClasses = cx(
    styles.count,
    unauthorized && styles.unauthorized,
  );

  const header = (
    <Menu.Header title={title}>
      {enableSearch && (
        <InputGroup>
          <InputGroup.Text
            icon="search"
            loading={loading}
            onChange={onChangeSearch}
            onClear={onClear}
            placeholder="Search..."
            value={value}
          />
        </InputGroup>
      )}
      {unauthorized && (
        <label className={styles.label}>
          <input
            checked={toggled}
            className={styles.checkbox}
            onChange={onChangeToggle}
            type="checkbox"
          />
          <span className={styles.text}>View only my pages</span>
        </label>
      )}
      {totalPages >= 0 && (
        <div className={countClasses}>
          {pages.length} of {totalPages} pages
        </div>
      )}
    </Menu.Header>
  );

  return (
    <Menu
      className={className}
      header={header}
    >
      {pages.length > 0 ? (
        pages.map(page => (
          <PagesMenuItem
            {...rest}
            key={page.id}
            page={page}
          />
        ))
      ) : (
        <Menu.Item componentClass="div">
          <Alert context="info">
            No pages found
          </Alert>
        </Menu.Item>
      )}
    </Menu>
  );
};

PagesMenu.propTypes = propTypes;
PagesMenu.defaultProps = defaultProps;

PagesMenu.fragments = {
  page: gql`
    fragment PagesMenuPage on Page {
      id
    }
  `,
};

export default PagesMenu;
